/*
 * Jira Ticket:
 * Created Date: Thu, 11th Jun 2020, 10:23:01 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useEffect, useState } from 'react';
import {
  Link,
  required,
  SimpleForm,
  TextInput,
  useDataProvider,
} from 'react-admin';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle, faPlusCircle, faUndo } from '@fortawesome/pro-regular-svg-icons';
import { Button, CircularProgress, Input, Snackbar } from '@material-ui/core';
import { Dialog, DialogTitle } from '@material-ui/core';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { ActionButton } from 'Components';
import getToken from 'utils/getToken';
import axios from 'axios';
import { validateFourStringCode } from 'utils/validation';
const validateCode = [required(), validateFourStringCode];

const UserList = props => {

  const dataProvider = useDataProvider();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [currentEvents, setCurrentEvents] = useState([]);

  const [eventCode, setEventCode] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');

  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState('');

  let page = 1;
  let perPage = 1000;

  const handleClick = (e) => {
    setCurrentEmail(e.email);
    setShowDialog(true);
    setCurrentEvents(e.events);
    setCurrentUserId(e.id);
  };

  const handleTypeClick = async (e) => {
    console.log(e);

    setLoading(true);

    const token = await getToken();

    axios({
      url: process.env.REACT_APP_GRAPHQL_URI,
      method: 'post',
      data: {
        query: `mutation updateUserType {updateUserType(id: "${e.id}", type: ${e.type == 'User' ? 1 : 0}) {id}}`
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => {
      getData();

      setAlert(true);
      setMessage('User type changed successfully');

    }).catch((err) => {
      console.log(err.message);
      setLoading(false);

      setAlert(true);
      setMessage('Error changing user type');
    });
  }

  const handleCloseClick = () => {
    setShowDialog(false);
  };

  const handleDeleteButton = async (eventCode) => {

    setLoading(true);
    const token = await getToken();

    axios({
      url: process.env.REACT_APP_GRAPHQL_URI,
      method: 'post',
      data: {
        query: `mutation revokeEvent {revokeEvent(eventCode: "${eventCode}", userId: "${currentUserId}") {id}}`
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => {
      getData();

      setAlert(true);
      setMessage('Event revoked successfully');

    }).catch((err) => {
      console.log(err.message);
      setLoading(false);

      setAlert(true);
      setMessage('Error revoking event');
    });
  };

  const handleAddEventButton = async () => {

    setLoading(true);
    const token = await getToken();

    axios({
      url: process.env.REACT_APP_GRAPHQL_URI,
      method: 'post',
      data: {
        query: `mutation assignEvent {assignEvent(eventCode: "${eventCode}", userId: "${currentUserId}", email:"${currentEmail}") {id}}`
      },

      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => {
      getData();

      setAlert(true);
      setMessage('Event added successfully');

    }).catch((err) => {
      console.log(err.message);
      setLoading(false);

      setAlert(true);
      setMessage('Error adding event');

    }).finally(() => {
      setEventCode('');
    });
  }

  const handleEventCodeChange = (e) => {
    setEventCode(e.target.value);
  }

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setAlert(false);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      type: 'string'
    },
    {
      field: 'type', headerName: 'Type',
      flex: 1,
      minWidth: 150,
      type: 'string'
    },
    {
      field: 'email', headerName: 'Email',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'subscribed', headerName: 'Subscribed',
      flex: 1,
      minWidth: 150,
      type: 'boolean'
    },
    {
      field: 'date_of_birth', headerName: 'Birthday',
      flex: 1,
      minWidth: 150,
      type: 'date'
    },
    {
      field: 'location', headerName: 'Location',
      flex: 1,
      minWidth: 150,
      type: 'string'
    },
    {
      field: 'eventCounts', headerName: 'Event counts',
      flex: 1,
      minWidth: 150,
      type: 'number'
    },
    {
      field: 'loyaltyStatus', headerName: 'Loyalty status',
      flex: 1,
      minWidth: 150,
      type: 'string'
    },
    {
      field: 'createdAt', headerName: 'Joined date',
      flex: 1,
      minWidth: 150,
      type: 'date'
    },
  ];

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);

    const token = await getToken();
    axios({
      url: process.env.REACT_APP_GRAPHQL_URI,
      method: 'post',
      data: {
        query: `query allUsers($page: Int, $perPage: Int, $filter: UserFilter) {
  items: allUsers(page: $page, perPage: $perPage, filter: $filter) {
    id
    type
    firstName
    lastName
    cognitoId
    location
    email
    date_of_birth
    events {
      id
      name
      event_code
      superSleuth {
        id
        stampClaimed
        sleuthEmail
        __typename
      }
      __typename
    }
    createdAt
    eventCounts
    couponCounts
    subscribed
    __typename
  }
  total: _allUsersMeta(page: $page, perPage: $perPage, filter: $filter) {
    count
    __typename
  }
}
`,
        operationName: 'allUsers',
        variables: {
          page: page,
          perPage: perPage,
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        const data = res.data.data.items.map((item) => {
          item.name = item.firstName + ' ' + item.lastName;
          if (item.date_of_birth) {
            item.date_of_birth = moment(item.date_of_birth, 'DD-MM-YYYY').format('YYYY-MM-DD');
          } else {
            item.date_of_birth = '';
          }
          item.superSleuth = item.superSleuth

          var count = 0;
          for (let i = 0; i < item.events.length; i++) {
            if (item.events[i].superSleuth) {
              count++;
            }
          }

          item.loyaltyStatus = item.couponCounts ? item.couponCounts + ' coupons' : 0 + ' coupons ';
          item.loyaltyStatus += ' (' + count + '/5 events)'
          item.type = item.type == 0 ? 'User' : 'Actor';
          return item;
        });
        setData(data);

        if (currentUserId) {
          setCurrentEvents(data.find((item) => item.id === currentUserId).events);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.log(err.message);
        setLoading(false);
      }).finally(() => {

        page += 1;
        getTheRestOfData(token, page, perPage);
      });
  }

  const getTheRestOfData = async (token, page, perPage) => {

    axios({
      url: process.env.REACT_APP_GRAPHQL_URI,
      method: 'post',
      data: {
        query: `query allUsers($page: Int, $perPage: Int, $filter: UserFilter) {
  items: allUsers(page: $page, perPage: $perPage, filter: $filter) {
    id
    type
    firstName
    lastName
    cognitoId
    location
    email
    date_of_birth
    events {
      id
      name
      event_code
      superSleuth {
        id
        stampClaimed
        sleuthEmail
        __typename
      }
      __typename
    }
    createdAt
    eventCounts
    couponCounts
    subscribed
    __typename
  }
  total: _allUsersMeta(page: $page, perPage: $perPage, filter: $filter) {
    count
    __typename
  }
}
`,
        operationName: 'allUsers',
        variables: {
          page: page,
          perPage: perPage,
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => {

      console.log(res.data.data.items.count, res.data.data.items);
      if (res.data.data.items.length > 0) {
        let moreData = res.data.data.items.map((item) => {
          item.name = item.firstName + ' ' + item.lastName;
          if (item.date_of_birth) {
            item.date_of_birth = moment(item.date_of_birth, 'DD-MM-YYYY').format('YYYY-MM-DD');
          } else {
            item.date_of_birth = '';
          }
          item.superSleuth = item.superSleuth

          var count = 0;
          for (let i = 0; i < item.events.length; i++) {
            if (item.events[i].superSleuth) {
              count++;
            }
          }

          item.loyaltyStatus = item.couponCounts ? item.couponCounts + ' coupons' : 0 + ' coupons ';
          item.loyaltyStatus += ' (' + count + '/5 events)'
          item.type = item.type == 0 ? 'User' : 'Actor';
          return item;
        });      

        setData(prev => [...prev, ...moreData]);

        getTheRestOfData(token, page + 1, perPage);
        if (currentUserId) {
          setCurrentEvents(moreData.find((item) => item.id === currentUserId).events);
        }
      } else {
        return;
      }
    }).catch((err) => {
      console.log(err.message);
    });

  }

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarExport />
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <Button onClick={getData}>
          <FontAwesomeIcon style={{ marginRight: '5px' }} icon={faUndo} /> Update data
        </Button>
      </GridToolbarContainer>
    );
  }

  return <>

    <h3 className='text-danger'>
      <FontAwesomeIcon icon={faExclamationCircle} /> &nbsp;
      This list uses external service to fetch data, data are synced once a day, you may see desynchronization.
    </h3>

    <DataGrid
      components={{
        Toolbar: CustomToolbar,
      }}
      rows={data}
      columns={columns}
      loading={loading}
      disableSelectionOnClick
      onCellClick={(cell) => {
        if (cell.field === 'eventCounts' || cell.field === 'loyaltyStatus') {
          handleClick(cell.row);
        } else if (cell.field === 'type') {
          handleTypeClick(cell.row);
        }
      }}
    />

    <Dialog open={showDialog} fullWidth
      onClose={handleCloseClick}
      aria-label={`User events details`}
    >

      {loading &&
        <CircularProgress style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '13%',
          marginLeft: '14%',
          position: 'fixed',
        }} size={60} />
      }
      <DialogTitle id="alert-dialog-title">
        User events details
      </DialogTitle>
      <div style={{ padding: '0px 16px 24px 20px' }}>
        {currentEvents.length === 0 ? <div> <p> No events found </p> </div>
          : currentEvents.map((event, index) => {
            return <div key={index}>
              <p> <Link to={`/event/${event.id}`}>{event.name} <b> [{event.event_code}]  </b>  </Link> &nbsp;

                {event.superSleuth &&
                  <ActionButton
                    disabled={loading}
                    label="Revoke"
                    onClick={(e) => handleDeleteButton(event.event_code)}
                    icon={<FontAwesomeIcon icon={faMinusCircle} />}
                  />
                }

              </p>
              <hr />
            </div>
          })
        }

        <Input
          placeholder='Event code'
          className='mr-3'
          onChange={(e) => handleEventCodeChange(e)}
          value={eventCode}
          disabled={loading}
        />
        <ActionButton
          label="Add new event or reassign event"
          onClick={handleAddEventButton}
          icon={<FontAwesomeIcon icon={faPlusCircle} />}
          disabled={loading}
        />

      </div>
    </Dialog>
    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={alert} autoHideDuration={6000} message={message} onClose={handleClose}></Snackbar>
  </>

};

export default UserList;
